/* eslint-disable array-element-newline */
/* eslint-disable new-cap */
import {MobxReactionUpdate} from '@adobe/lit-mobx';
import {LitElement, html, nothing} from 'lit';
import {classMap} from 'lit/directives/class-map.js';
import {createRef, ref} from 'lit/directives/ref.js';
import styles from './diamond-card.component.scss?as=stylesheet';
import {defineCustomElement} from 'extensions/utils';
import Splide from '@splidejs/splide';
import {DIAMOND_NOT_FOUND_IMAGE, DIAMOND_PLACEHOLDER, DIAMOND_IMAGES, DIAMOND_SIDES, DIAMOND_TOPS} from '../../data/constants';
import waitForExpression from '@mvps-genesis/wait-for-expression';

class DiamondCard extends MobxReactionUpdate(LitElement) {

	static get styles () {
		return [styles];
	}

	static get properties () {
		return {
			currentImage: {
				type: String
			},
			imageLoaded: {
				state: true,
				type: Boolean
			},
			images: {
				type: Array
			},
			mainImage: {
				type: String
			},
			purchaseFormSubmitted: {
				type: Boolean
			},
			result: {
				type: Object
			},
			store: {
				state: true,
				type: Object
			}
		};
	}

	slider = createRef();

	constructor () {
		super();

		this.imageLoaded = false;
		this.modal = null;
		this.purchaseFormSubmitted = false;
		this.imageSlider = null;
		this.images = [];

	}

	connectedCallback () {
		super.connectedCallback();
		this.createImageList();
	}

	disconnectedCallback () {
		this.removeEvents();
	}

	removeEvents () {
		if (this.imageSlider) {
			this.imageSlider.destroy({
				completely: true
			});
		}
	}

	updated (changedProperties) {
		super.updated();

		if (changedProperties.has('result') && changedProperties.result?.value?.id !== this.result?.id) {
			this.onResultUpdate();
		}

		this.checkCompares();
	}

	checkCompares () {
		const compareCheckboxes = this.shadowRoot?.querySelectorAll('.diamond-card--compare-input');

		for (const compareCheckbox of compareCheckboxes) {
			compareCheckbox.checked = Boolean(this.compareChecked);
		}
	}

	async onResultUpdate () {
		await this.updateComplete;

		this.currentImage = this.mainImage;
		this.removeEvents();
		this.createImageList();
		this.closeModal();
	}

	get mainImage () {
		return DIAMOND_IMAGES[this.diamondShape];
	}

	get isSelectedDiamond () {
		return this.store?.diamond?.product?.id && this.store?.diamond?.product?.id === this.result?.id;
	}

	firstUpdated () {
		this.modal = this.shadowRoot.querySelector('#diamond-card-modal');

		waitForExpression(
			() => this.store?.loaded,
			() => {
				if (this.store?.scrollDiamondIntoView && this.store?.diamond?.product?.id === this.result?.id) {
					this.scrollIntoView({behavior: 'smooth'});
					this.store?.setScrollDiamondIntoView(null);
				}
			}
		);
	}

	toggleCompareListProduct () {
		this.store.diamond.toggleCompareListProduct(this.result);
	}

	// eslint-disable-next-line max-lines-per-function
	render () {
		const classes = {
			'diamond-card--image-loaded': this.imageLoaded,
			'diamond-card--inner': true,
			'diamond-modal-open': this.store?.diamond?.openModal === this.modal,
			'diamond-selected': this.isSelectedDiamond
		};

		return html`
			<div class="${classMap(classes)}" data-id="${this.result?.id}">
				<div class="diamond-card--image-wrap">
					${this.renderImage()}
					${this.renderSignatureFlag()}
				</div>
				<div class="diamond-card--compare__large">
					<label class="diamond-card__compare" for="js-compare-${this.result.id}-large" aria-label="${this.compareChecked ? 'Remove Diamond from Compare' : 'Add Diamond to Compare'}" title="${this.compareChecked ? 'Remove Diamond from Compare' : 'Add Diamond to Compare'}">
						<input class="diamond-card--compare-input" @change="${this.toggleCompareListProduct}" type="checkbox" id="js-compare-${this.result.id}-large" ?checked="${this.compareChecked}" />
						<span class="diamond-card__compare-text">&nbsp;</span>
					</label>
				</div>
				<div class="diamond-card--details">
					<div class="diamond-card--detail diamond-card--details-shape">
						<div class="diamond-card--details__title">Shape</div>
						<div class="diamond-card--details__value">${this.result?.attributes?.shape ?? 'N/A'}</div>
					</div>
					<div class="diamond-card--detail diamond-card--details-price">
						<div class="diamond-card--details__title">Price</div>
						<div class="diamond-card--details__value">${this.renderPricing()}</div>
					</div>
					<div class="diamond-card--detail diamond-card--details-carat">
						<div class="diamond-card--details__title">Carat</div>
						<div class="diamond-card--details__value">${this.formattedCarat ?? 'N/A'}</div>
					</div>
					<div class="diamond-card--detail diamond-card--details-color">
						<div class="diamond-card--details__title">Color</div>
						<div class="diamond-card--details__value">${this.result?.attributes?.color ?? 'N/A'}</div>
					</div>
					<div class="diamond-card--detail diamond-card--details-cut">
						<div class="diamond-card--details__title">Cut</div>
						<div class="diamond-card--details__value">${this.result?.attributes?.cut ?? 'N/A'}</div>
					</div>
					<div class="diamond-card--detail diamond-card--details-clarity">
						<div class="diamond-card--details__title">Clarity</div>
						<div class="diamond-card--details__value">${this.result?.attributes?.clarity ?? 'N/A'}</div>
					</div>
				</div>
				<div class="diamond-card--select">
					<div class="diamond-card--compare__small">
						<label class="diamond-card__compare" for="js-compare-${this.result.id}-small" aria-label="${this.compareChecked ? 'Remove Diamond from Compare' : 'Add Diamond to Compare'}" title="${this.compareChecked ? 'Remove Diamond from Compare' : 'Add Diamond to Compare'}">
							<input class="diamond-card--compare-input" @change="${this.toggleCompareListProduct}" type="checkbox" id="js-compare-${this.result.id}-small" ?checked="${this.compareChecked}" />
							<span class="diamond-card__compare-text">Add to Compare</span>
						</label>
					</div>
					<button class="diamond-card-button diamond-card--select-button" @click="${() => this.openModal()}">${this.isSelectedDiamond ? 'Selected' : 'Select'}</button>
				</div>
			</div>
			<rb-diamond-modal
				id="diamond-card-modal"
				lock="${this.store?.screenSize === 'desktop' ? 'false' : 'true'}"
				overlay="${this.store?.screenSize === 'desktop' ? 'false' : 'true'}"
				direction="${this.store?.screenSize === 'desktop' ? 'inline' : 'right'}"
				animation="fade"
				header="false"
				content-padding="false"
			>
				<div slot="content">${this.renderModalContent()}</div>
			</rb-diamond-modal>
		`;
	}

	renderModalContent () {
		return html`
			<div class="diamond-card-modal--inner">
				${this.renderModalImages()}
				${this.renderModalInformation()}
			</div>
		`;
	}

	renderModalImages () {
		const imageOutput = [];

		for (const image of this.images) {
			if (image.type === 'video') {
				imageOutput.push(html`
					<div class="diamond-card--slide diamond-card--slide-${image.type} splide__slide" data-type="${image.type}">
						<div class="diamond-card--slide--video">
							<iframe class="diamond-card--slide-iframe" data-src="${image.src}" frameborder="0" scrolling="no" class="diamond-card--video" allowfullscreen></iframe>
						</div>
					</div>
				`);
			} else {
				imageOutput.push(html`
					<div class="diamond-card--slide diamond-card--slide-${image.type} splide__slide" data-type="${image.type}">
						<div class="diamond-card--slide-inner">
							<img src="${DIAMOND_PLACEHOLDER}" data-splide-lazy="${image.src}" alt="${image.alt}" />
							<div class="diamond-card-modal--sample-text">
								Sample Image Only
							</div>
						</div>
					</div>
				`);
			}
		}

		return html`
			<div class="diamond-card-modal--image-wrapper diamond-card-modal--slider">
				<div class="js-image-carousel diamond-card--images splide" ${ref(this.slider)}>
					<div class="splide__track">
						<div class="splide__list">
							${imageOutput}
						</div>
					</div>
					<div class="diamond-card-modal--image-details">
						<div class="diamond-card-modal--stock-number">
							<strong class="diamond-card-modal--stock-label">Stock #</strong>
							${this.result?.sku}
						</div>
					</div>
				</div>
			</div>
		`;
	}

	renderModalInformation () {
		return html`
			<div class="diamond-card-modal--detail-wrapper">
				${this.renderModalDetails()}
				${this.renderModalCTAs()}
			</div>
		`;
	}

	// eslint-disable-next-line max-lines-per-function
	renderModalDetails () {
		return html`
			<div class="diamond-card-modal--details-wrapper">
				<div class="diamond-card-modal--details">
					<div class="diamond-card-modal--detail diamond-card-modal--details-shape">
						<div class="diamond-card--details__title">Shape</div>
						<div class="diamond-card--details__value">${this.result?.attributes?.shape ?? 'N/A'}</div>
					</div>
					<div class="diamond-card-modal--detail diamond-card-modal--details-price">
						<div class="diamond-card--details__title">Price</div>
						<div class="diamond-card--details__value">${this.renderPricing()}</div>
					</div>
					<div class="diamond-card-modal--detail diamond-card-modal--details-carat">
						<div class="diamond-card--details__title">Carat</div>
						<div class="diamond-card--details__value">${this.formattedCarat ?? 'N/A'}</div>
					</div>
					<div class="diamond-card-modal--detail diamond-card-modal--details-color">
						<div class="diamond-card--details__title">Color</div>
						<div class="diamond-card--details__value">${this.result?.attributes?.color ?? 'N/A'}</div>
					</div>
					<div class="diamond-card-modal--detail diamond-card-modal--details-cut">
						<div class="diamond-card--details__title">Cut</div>
						<div class="diamond-card--details__value">${this.result?.attributes?.cut ?? 'N/A'}</div>
					</div>
					<div class="diamond-card-modal--detail diamond-card-modal--details-clarity">
						<div class="diamond-card--details__title">Clarity</div>
						<div class="diamond-card--details__value">${this.result?.attributes?.clarity ?? 'N/A'}</div>
					</div>
					<div class="diamond-card-modal--detail diamond-card-modal--detail-small diamond-card-modal--details-polish">
						<div class="diamond-card--details__title">Polish</div>
						<div class="diamond-card--details__value">${this.result?.attributes?.polish ?? 'N/A'}</div>
					</div>
					<div class="diamond-card-modal--detail diamond-card-modal--detail-small diamond-card-modal--details-fluorescence">
						<div class="diamond-card--details__title">Fluor.</div>
						<div class="diamond-card--details__value">${this.result?.attributes?.fluorescence ?? 'N/A'}</div>
					</div>
					<div class="diamond-card-modal--detail diamond-card-modal--detail-small diamond-card-modal--details-symmetry">
						<div class="diamond-card--details__title">Symm.</div>
						<div class="diamond-card--details__value">${this.result?.attributes?.symmetry ?? 'N/A'}</div>
					</div>
				</div>
			</div>
			<div class="diamond-card-modal--details-wrapper diamond-card-modal--additional-detail">
				<div class="diamond-card-modal--additional-detail__title">Additional Details</div>
				<div class="diamond-card-modal--additional-details diamond-card-modal--details">
					<div class="diamond-card-modal--detail diamond-card-modal--detail-large diamond-card-modal--details-polish">
						<div class="diamond-card--details__title">Polish</div>
						<div class="diamond-card--details__value">${this.result?.attributes?.polish ?? 'N/A'}</div>
					</div>
					<div class="diamond-card-modal--detail diamond-card-modal--detail-large diamond-card-modal--details-fluorescence">
						<div class="diamond-card--details__title">Fluor.</div>
						<div class="diamond-card--details__value">${this.result?.attributes?.fluorescence ?? 'N/A'}</div>
					</div>
					<div class="diamond-card-modal--detail diamond-card-modal--detail-large diamond-card-modal--details-symmetry">
						<div class="diamond-card--details__title">Symm.</div>
						<div class="diamond-card--details__value">${this.result?.attributes?.symmetry ?? 'N/A'}</div>
					</div>
					<div class="diamond-card-modal--detail diamond-card-modal--details-measurements">
						<div class="diamond-card--details__title">Measurements</div>
						<div class="diamond-card--details__value">${this.formattedMeasurement ?? 'N/A'}</div>
					</div>
					<div class="diamond-card-modal--detail diamond-card-modal--details-table">
						<div class="diamond-card--details__title">Table</div>
						<div class="diamond-card--details__value">${this.result?.attributes?.table ? this.result.attributes.table + '%' : 'N/A'}</div>
					</div>
					<div class="diamond-card-modal--detail diamond-card-modal--details-depth">
						<div class="diamond-card--details__title">Depth</div>
						<div class="diamond-card--details__value">${this.result?.attributes?.depth ?? 'N/A'}</div>
					</div>
					<div class="diamond-card-modal--detail diamond-card-modal--detail-small diamond-card-modal--details-origin">
						<div class="diamond-card--details__title">Origin</div>
						<div class="diamond-card--details__value">${this.result?.attributes?.origin ?? 'N/A'}</div>
					</div>
				</div>
			</div>
			<div class="diamond-card-modal--report-signature">
				<div class="diamond-card-modal--reports diamond-card-modal--report-diamond-id">
					<div class="diamond-card-modal--reports__title">Diamond SKU</div>
					<div class="diamond-card-modal--reports__value">${this.result?.sku}</div>
				</div>
				${this.renderReports()}
				${this.renderSignature()}
			</div>
		`;
	}

	renderReports () {
		if (!this.result?.attributes?.certificateurl) {
			return nothing;
		}

		return html`
			<div class="diamond-card-modal--reports diamond-card-modal--report-signature-item">
				<div class="diamond-card-modal--reports__title">Reports</div>
				<div class="diamond-card-modal--reports__value">
					<a href="${this.result?.attributes?.certificateurl}" class="diamond-card-modal--report-link" target="_blank" title="${this.result?.attributes?.cert}">
						${this.result?.attributes?.cert}
					</a>
					<span class="diamond-card-modal--diamondid">#${this.result?.id}</span>
				</div>
			</div>
		`;
	}

	renderSignature () {
		if (!this.result?.attributes?.shape?.includes('Signature')) {
			return nothing;
		}

		return html`
			<div class="diamond-card-modal--signature diamond-card-modal--report-signature-item">
				<div class="diamond-card-modal--signature__title">Borsheims Collection</div>
				<div class="diamond-card-modal--signature__value">
					<span class="icon-signature diamond-card-modal--signature__icon" aria-hidden="true"></span>
					<span class="diamond-card-modal--signature__text">Signature Select</span>
				</div>
			</div>
		`;
	}

	get selectDiamondHref () {
		const baseUrl = this.store?.setting?.product?.id
			? this.store?.reviewStepHref
			: this.store?.ringBuilderUrl;

		const url = new URL(baseUrl, window.location.origin);
		for (let [key, value] of this.store?.urlSearchParams) {
			url.searchParams.set(key, value);
		}
		url.searchParams.set('diamond', this.result?.id);

		return url.toString();
	}

	renderModalCTAs () {
		return html`
			<div class="diamond-card-modal--footer">
				<div class="diamond-card-modal--ctas">
					<div class="diamond-card-modal--cta">
						<a href="${this.selectDiamondHref}" @click="${() => this.trackSelected()}" class="diamond-card-button-primary diamond-card-modal-button diamond-card-modal-button--select" ?disabled="${this.isSelectedDiamond}">${this.isSelectedDiamond ? 'Selected' : 'Choose This Diamond'}</a>
					</div>
					<form @submit="${this.handlePurchaseSubmit}" class="diamond-card-modal--cta diamond-card-modal-form" action="${window?.mivaJS?.Basket_Url}" method="POST">
						<input type="hidden" name="Custom_Action" value="ADPR_Diamond">
						<input type="hidden" name="DID" value="${this.result?.sku}">
						<input type="hidden" name="Diamond_ID" value="${this.result?.id}">
						<input type="hidden" name="Lab_Diamond" value="${this.result?.attributes?.labgrown}">
						<input type="hidden" name="Quantity" value="1">
						<button class="diamond-card-button diamond-card-modal-button diamond-card-modal-button--buy" type="submit" ?disabled="${this.purchaseFormSubmitted}">
							${this.purchaseFormSubmitted ? 'Loading...' : 'Buy Diamond Only'}
						</button>
					</form>
				</div>
				<div class="diamond-card-modal--bottom">
					<label class="diamond-card__compare" for="js-compare-${this.result.id}-modal" aria-label="${this.compareChecked ? 'Remove Diamond from Compare' : 'Add Diamond to Compare'}" title="${this.compareChecked ? 'Remove Diamond from Compare' : 'Add Diamond to Compare'}">
						<input class="diamond-card--compare-input" @change="${this.toggleCompareListProduct}" type="checkbox" id="js-compare-${this.result.id}-modal" ?checked="${this.compareChecked}" />
						<span class="diamond-card__compare-text">Add to Compare</span>
					</label>
					<rb-chat-widget hide-trigger class="diamond-card-modal--expert">
						<button slot="trigger" class="diamond-card-modal--expert-trigger">Talk to an Expert</button>
					</rb-chat-widget>
				</div>
			</div>
		`;
	}

	handlePurchaseSubmit () {
		this.purchaseFormSubmitted = true;

		window.dataLayer = window.dataLayer || [];
		window.dataLayer.push({
			event: 'ga_event',
			/* eslint-disable camelcase */
			ga_event_action: 'Add to Cart',
			ga_event_category: 'Ring Builder',
			ga_event_label: 'Diamond',
			ga_event_non_interaction: false
			/* eslint-enable camelcase */
		});

		return true;
	}

	renderImage () {
		return html`
			<span class="diamond-card--image" @click="${() => this.openModal()}">
				<div class="diamond-card--image-loader splide__spinner"></div>
				<img src="${this.currentImage}" alt="${this.diamondName}" @error="${this.handleImageError}" @load="${this.handleImageLoad}" ${ref(this.imageContainerChanged)} class="diamond-card--image-img" height="40" width="40" loading="lazy" />
			</span>
		`;
	}

	renderSignatureFlag () {
		const flagClasses = {
			'diamond-card--signature': true,
			'icon-signature': this.result?.attributes?.shape?.includes('Signature')
		};

		return html`
			<span class="${classMap(flagClasses)}" aria-hidden="true"></span>
		`;
	}

	get formattedCarat () {
		return parseFloat(this.result?.attributes?.carat).toFixed(2);
	}

	get formattedMeasurement () {
		if (!this.result?.attributes?.measurement) {
			return null;
		}

		let measurement = this.result.attributes.measurement.trim().toUpperCase();

		if (measurement.endsWith('X')) {
			measurement = measurement.slice(0, measurement.length - 1);
		}

		return measurement?.split('X').join(' X ');
	}

	renderPricing () {
		return html`
			<div class="diamond-card--price">${this.result?.formattedPrice}</div>
		`;
	}

	get compareChecked () {
		return Boolean(this.store?.diamond?.compareList?.find(compareProduct => compareProduct?.id === this.result?.id));
	}

	get diamondName () {
		return this.formattedCarat + ' Carat ' + this.result?.attributes?.shape;
	}

	get diamondShape () {
		return this.result?.attributes?.shape?.toLowerCase().replace(' ', '-');
	}

	openModal () {
		if (!this.modal) {
			return;
		}

		if (this.modal.isOpen) {
			this.closeModal();
			return;
		}

		if (this.store?.diamond?.openModal) {
			this.store.diamond.openModal.close();
		}

		if (this.store?.diamond) {
			this.store.diamond.setOpenModal(this.modal);
		}

		this.modal.open();
		this.initSlider();
	}

	closeModal () {
		if (!this.modal || !this.modal.isOpen) {
			return;
		}

		if (this.store?.diamond?.openModal) {
			this.store.diamond.openModal.close();
		}

		if (this.store?.diamond) {
			this.store.diamond.setOpenModal(null);
		}
	}

	createImageList () {
		const images = [];

		images.push({
			alt: 'Diamond Image',
			src: DIAMOND_IMAGES[this.diamondShape],
			type: 'diamond-image'
		});

		if (this.result?.attributes?.videofilename) {
			images.push({
				alt: 'Diamond Video',
				src: this.result.attributes.videofilename,
				type: 'video'
			});
		}

		images.push({
			alt: 'Diamond Side View',
			src: DIAMOND_SIDES[this.diamondShape],
			type: 'image'
		});

		images.push({
			alt: 'Diamond Top View',
			src: DIAMOND_TOPS[this.diamondShape],
			type: 'image'
		});

		this.images = [...images];
	}

	// eslint-disable-next-line max-lines-per-function
	initSlider () {
		if (this.imageSlider) {
			this.imageSlider.destroy({
				completely: true
			});

			this.imageSlider = null;
		}

		if (!this.slider?.value) {
			return;
		}

		setTimeout(() => {
			this.imageSlider = new Splide(this.slider.value, {
				arrows: false,
				lazyLoad: 'nearby',
				pagination: true,
				start: 0,
				type: 'loop'
			});

			this.imageSlider.on('pagination:mounted', (data) => {
				for (let i = 0; i < data?.items?.length; i++) {
					const item = data.items[i];
					const image = this.images[i];

					if (image?.type === 'video') {
						item.button.classList.add('diamond-card-pagination--video');
						item.button.innerHTML = `
							<span class="diamond-card-page--inner">
								<span class="icon-svg-play diamond-card--thumbnail-icon" aria-hidden="true"></span>
							</span>
						`;
					} else {
						item.button.classList.add('diamond-card-pagination--image', `diamond-card-pagination--image-${image.type}`);
						item.button.innerHTML = `
							<span class="diamond-card-page--inner">
								<img src="${image.src}">
							</span>
						`;
					}
				}
			});

			// Ondemand load the iframe
			this.imageSlider.on('active', (slide) => {
				const iframe = slide?.slide?.querySelector('.diamond-card--slide-iframe');
				if (iframe && !iframe.src) {
					iframe.src = iframe.getAttribute('data-src');
				}
			});

			this.imageSlider.mount();
		}, 10);
	}

	imageContainerChanged (container) {
		if (!container) {
			return;
		}

		const observer = new MutationObserver((changes) => {
			changes.forEach(change => {
				if (change.attributeName.includes('src')) {
					this.imageLoaded = container.complete;
				}
			});
		});

		observer.observe(container, {attributes: true});
	}

	handleImageLoad () {
		this.imageLoaded = true;
	}

	handleImageError () {
		this.currentImage = DIAMOND_NOT_FOUND_IMAGE;
	}

	trackSelected () {
		window.dataLayer = window.dataLayer || [];
		window.dataLayer.push({
			event: 'ga_event',
			/* eslint-disable camelcase */
			ga_event_action: 'Selection',
			ga_event_category: 'Ring Builder',
			ga_event_label: 'Diamond',
			ga_event_non_interaction: false
			/* eslint-enable camelcase */
		});
	}
};

defineCustomElement('rb-diamond-card', DiamondCard);
